import { useMemo, useState } from "react";
import { formatCompact, formatNumber } from "../utils/format";

export interface SankeyLink {
  source: string;
  target: string;
  value: number;
}

export interface SankeyData {
  left: string[];
  right: string[];
  links: SankeyLink[];
}

interface SankeyChartProps {
  data?: SankeyData;
  loading?: boolean;
  leftLabels?: Record<string, string>;
  rightLabels?: Record<string, string>;
  leftColors?: Record<string, string>;
  rightColors?: Record<string, string>;
}

const OTHERS = "others";
const UNKNOWN = "unknown";
const LEFT_PALETTE = [
  "#22C55E",
  "#A78BFA",
  "#60A5FA",
  "#FB923C",
  "#F472B6",
  "#FACC15",
  "#2DD4BF",
  "#818CF8",
];
const RIGHT_PALETTE = [
  "#34D399",
  "#60A5FA",
  "#A78BFA",
  "#FB923C",
  "#F472B6",
  "#2DD4BF",
  "#FACC15",
  "#818CF8",
];
const NODE_WIDTH = 16;
const NODE_PAD = 25;
const CENTER_WIDTH = 480;
const LEFT_LABEL_COL = 140;
const RIGHT_LABEL_COL = 140;

interface PlacedNode {
  name: string;
  value: number;
  y: number;
  height: number;
  color: string;
}

interface PlacedLink extends SankeyLink {
  sy0: number;
  sy1: number;
  ty0: number;
  ty1: number;
  color: string;
}

interface LayoutResult {
  leftNodes: PlacedNode[];
  rightNodes: PlacedNode[];
  links: PlacedLink[];
  height: number;
}

function hasPersian(text: string): boolean {
  return /[\u0600-\u06FF]/.test(text);
}

function displayLabel(id: string, labels?: Record<string, string>): string {
  if (id === OTHERS) return "سایر";
  if (id === UNKNOWN) return "نامشخص";
  if (labels && labels[id]) return labels[id];
  return id;
}

function buildLayout(
  data: SankeyData,
  leftColors: Map<string, string>,
  rightColors: Map<string, string>
): LayoutResult {
  const leftVals = new Map<string, number>();
  const rightVals = new Map<string, number>();
  for (const link of data.links) {
    leftVals.set(link.source, (leftVals.get(link.source) || 0) + link.value);
    rightVals.set(link.target, (rightVals.get(link.target) || 0) + link.value);
  }

  const leftNames = data.left
    .filter((name) => (leftVals.get(name) || 0) > 0)
    .sort((a, b) => leftVals.get(b)! - leftVals.get(a)!);
  const rightNames = data.right
    .filter((name) => (rightVals.get(name) || 0) > 0)
    .sort((a, b) => rightVals.get(b)! - rightVals.get(a)!);

  const total = leftNames.reduce((sum, name) => sum + leftVals.get(name)!, 0);
  const nodeCount = Math.max(leftNames.length, rightNames.length, 1);
  const height = Math.min(440, Math.max(220, nodeCount * 56));
  const scale = total > 0 ? (height - NODE_PAD * (nodeCount - 1)) / total : 1;

  const placeNodes = (
    names: string[],
    vals: Map<string, number>,
    colors: Map<string, string>,
    palette: string[]
  ): PlacedNode[] => {
    let cursor = 0;
    return names.map((name, i) => {
      const value = vals.get(name)!;
      const node = {
        name,
        value,
        y: cursor,
        height: Math.max(value * scale, 1.5),
        color: colors.get(name) || palette[i % palette.length],
      };
      cursor += node.height + NODE_PAD;
      return node;
    });
  };

  const leftNodes = placeNodes(leftNames, leftVals, leftColors, LEFT_PALETTE);
  const rightNodes = placeNodes(rightNames, rightVals, rightColors, RIGHT_PALETTE);
  const leftBy = new Map(leftNodes.map((node) => [node.name, node]));
  const rightBy = new Map(rightNodes.map((node) => [node.name, node]));

  const rightIndex = new Map(rightNames.map((name, i) => [name, i]));
  const bySource = new Map<string, SankeyLink[]>();
  for (const link of data.links) {
    if (!leftVals.get(link.source) || !rightVals.get(link.target)) continue;
    const arr = bySource.get(link.source) || [];
    arr.push(link);
    bySource.set(link.source, arr);
  }
  for (const arr of bySource.values()) {
    arr.sort((a, b) => rightIndex.get(a.target)! - rightIndex.get(b.target)!);
  }

  const linksByKey = new Map<string, PlacedLink>();
  const sourceCursor = new Map<string, number>();
  for (const name of leftNames) {
    sourceCursor.set(name, leftBy.get(name)!.y);
    for (const link of bySource.get(name) || []) {
      const y0 = sourceCursor.get(name)!;
      const slice = Math.max(link.value * scale, 0.8);
      const placed: PlacedLink = {
        ...link,
        sy0: y0,
        sy1: y0 + slice,
        ty0: 0,
        ty1: 0,
        color: leftBy.get(name)!.color,
      };
      linksByKey.set(`${link.source}|${link.target}`, placed);
      sourceCursor.set(name, y0 + slice);
    }
  }

  const leftIndex = new Map(leftNames.map((name, i) => [name, i]));
  const byTarget = new Map<string, SankeyLink[]>();
  for (const link of data.links) {
    if (!leftVals.get(link.source) || !rightVals.get(link.target)) continue;
    const arr = byTarget.get(link.target) || [];
    arr.push(link);
    byTarget.set(link.target, arr);
  }
  for (const arr of byTarget.values()) {
    arr.sort((a, b) => leftIndex.get(a.source)! - leftIndex.get(b.source)!);
  }

  const targetCursor = new Map<string, number>();
  for (const name of rightNames) {
    targetCursor.set(name, rightBy.get(name)!.y);
    for (const link of byTarget.get(name) || []) {
      const placed = linksByKey.get(`${link.source}|${link.target}`);
      if (!placed) continue;
      const y0 = targetCursor.get(name)!;
      const slice = Math.max(link.value * scale, 0.8);
      placed.ty0 = y0;
      placed.ty1 = y0 + slice;
      targetCursor.set(name, y0 + slice);
    }
  }

  return { leftNodes, rightNodes, links: [...linksByKey.values()], height };
}

function linkPath(link: PlacedLink): string {
  const x0 = NODE_WIDTH;
  const x1 = CENTER_WIDTH - NODE_WIDTH;
  const mx = (x0 + x1) / 2;
  return (
    `M ${x0} ${link.sy0} ` +
    `C ${mx} ${link.sy0}, ${mx} ${link.ty0}, ${x1} ${link.ty0} ` +
    `L ${x1} ${link.ty1} ` +
    `C ${mx} ${link.ty1}, ${mx} ${link.sy1}, ${x0} ${link.sy1} Z`
  );
}

function NodeColumn({
  nodes,
  labels,
  side,
  width,
  height,
  hoveredNode,
  onHover,
}: {
  nodes: PlacedNode[];
  labels?: Record<string, string>;
  side: "left" | "right";
  width: number;
  height: number;
  hoveredNode: string | null;
  onHover: (name: string | null) => void;
}) {
  const isLeft = side === "left";
  return (
    <div className="relative shrink-0" style={{ width, height }}>
      {nodes.map((node) => {
        const label = displayLabel(node.name, labels);
        return (
          <div
            key={node.name}
            className={`absolute left-0 right-0 flex items-center gap-1.5 ${
              isLeft ? "justify-end" : "justify-start"
            }`}
            style={{
              top: node.y,
              height: node.height,
              opacity: hoveredNode && hoveredNode !== node.name ? 0.35 : 1,
              transition: "opacity 150ms",
            }}
            onMouseEnter={() => onHover(node.name)}
            onMouseLeave={() => onHover(null)}
          >
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: node.color }}
            />
            <div className="min-w-0 leading-tight" dir={hasPersian(label) ? "rtl" : "ltr"}>
              <p className="text-[11px] text-text-main truncate" title={label}>
                {label}
              </p>
              <p className="text-[10px] text-text-tertiary">
                {formatCompact(node.value)} توکن
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function SankeyChart({
  data,
  loading,
  leftLabels,
  rightLabels,
  leftColors,
  rightColors,
}: SankeyChartProps) {
  const [hoveredLink, setHoveredLink] = useState<PlacedLink | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const colorMaps = useMemo(() => {
    const left = new Map<string, string>();
    const right = new Map<string, string>();
    (data?.left || []).forEach((name, i) =>
      left.set(name, (leftColors && leftColors[name]) || LEFT_PALETTE[i % LEFT_PALETTE.length])
    );
    (data?.right || []).forEach((name, i) =>
      right.set(name, (rightColors && rightColors[name]) || RIGHT_PALETTE[i % RIGHT_PALETTE.length])
    );
    return { left, right };
  }, [data, leftColors, rightColors]);

  const layout = useMemo<LayoutResult | null>(
    () => (data ? buildLayout(data, colorMaps.left, colorMaps.right) : null),
    [data, colorMaps]
  );

  if (loading) {
    return (
      <div className="h-[260px] flex items-center justify-center">
        <div className="h-44 w-full bg-surface-hover rounded animate-pulse" />
      </div>
    );
  }

  if (!layout || !layout.links.length) {
    return (
      <div className="h-[240px] flex items-center justify-center text-text-tertiary">
        <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  const hasDim = hoveredLink !== null || hoveredNode !== null;
  const isDimmed = (link: PlacedLink) =>
    hasDim &&
    !(
      (hoveredLink && hoveredLink.source === link.source && hoveredLink.target === link.target) ||
      (hoveredNode && (link.source === hoveredNode || link.target === hoveredNode))
    );

  return (
    <div dir="ltr" className="flex items-stretch">
      <NodeColumn
        nodes={layout.leftNodes}
        labels={leftLabels}
        side="left"
        width={LEFT_LABEL_COL}
        height={layout.height}
        hoveredNode={hoveredNode}
        onHover={setHoveredNode}
      />
      <svg
        className="flex-1"
        viewBox={`0 0 ${CENTER_WIDTH} ${layout.height}`}
        style={{ height: layout.height }}
        preserveAspectRatio="none"
      >
        {layout.links.map((link, i) => (
          <path
            key={i}
            d={linkPath(link)}
            fill={link.color}
            opacity={isDimmed(link) ? 0.12 : 0.6}
            onMouseEnter={() => setHoveredLink(link)}
            onMouseLeave={() => setHoveredLink(null)}
            style={{ transition: "opacity 150ms", cursor: "pointer" }}
          >
            <title>{`${displayLabel(link.source, leftLabels)} ← ${displayLabel(
              link.target,
              rightLabels
            )}: ${formatNumber(link.value)} توکن`}</title>
          </path>
        ))}
        {layout.leftNodes.map((node) => (
          <rect
            key={node.name}
            x={0}
            y={node.y}
            width={NODE_WIDTH}
            height={node.height}
            rx={3}
            fill={node.color}
            onMouseEnter={() => setHoveredNode(node.name)}
            onMouseLeave={() => setHoveredNode(null)}
            style={{ cursor: "pointer" }}
          >
            <title>{`${displayLabel(node.name, leftLabels)}: ${formatCompact(node.value)} توکن`}</title>
          </rect>
        ))}
        {layout.rightNodes.map((node) => (
          <rect
            key={node.name}
            x={CENTER_WIDTH - NODE_WIDTH}
            y={node.y}
            width={NODE_WIDTH}
            height={node.height}
            rx={3}
            fill={node.color}
            onMouseEnter={() => setHoveredNode(node.name)}
            onMouseLeave={() => setHoveredNode(null)}
            style={{ cursor: "pointer" }}
          >
            <title>{`${displayLabel(node.name, rightLabels)}: ${formatCompact(node.value)} توکن`}</title>
          </rect>
        ))}
      </svg>
      <NodeColumn
        nodes={layout.rightNodes}
        labels={rightLabels}
        side="right"
        width={RIGHT_LABEL_COL}
        height={layout.height}
        hoveredNode={hoveredNode}
        onHover={setHoveredNode}
      />
    </div>
  );
}
